import { onMounted, onUnmounted, nextTick, watch } from 'vue';
import type { Ref } from 'vue';

function useFocusTrap(
  containerRef: Ref<HTMLElement | null>,
  isActive: Ref<boolean>,
  callback?: () => void,
) {
  const focusable =
    'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), *[tabindex="1"]';
  const focusFirstElement = async () => {
    await nextTick();

    if (!isActive.value || !containerRef.value) return;

    const focusableElements = containerRef.value.querySelectorAll<HTMLElement>(focusable);

    const firstElement = focusableElements[0];
    if (firstElement) {
      firstElement.focus();
    }
  };

  const handleKeydown = async (event: KeyboardEvent) => {
    await nextTick(); // Safari fix

    if (!isActive.value || !containerRef.value) return;

    const focusableElements = containerRef.value.querySelectorAll<HTMLElement>(focusable);

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.key === 'Tab') {
      const active = document.activeElement as HTMLElement;

      if (event.shiftKey && active === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && active === lastElement) {
        event.preventDefault();
        firstElement.focus();
      } else if (!Array.from(focusableElements).includes(active)) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  };

  watch(
    isActive,
    (value) => {
      if (value) {
        focusFirstElement();
      } else if (callback) {
        callback();
      }
    },
    { immediate: true },
  );

  onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
  });
}

export default useFocusTrap;
